// Saved Items View - Saved questions and cigars
import React, { useState } from 'react';
import { Search, Plus, Trash2, ChevronLeft, Bookmark, MessageSquare, Star } from 'lucide-react';
import { CigarRecommendation } from '../data/festivals';

interface SavedItemsViewProps {
  savedQuestions: string[];
  savedCigars: CigarRecommendation[];
  onDeleteQuestion: (index: number) => void;
  onDeleteCigar: (cigarId: string) => void;
  onAddCustomQuestion: (question: string) => void;
  onViewCigar: (cigar: CigarRecommendation) => void;
  onDiscoverCigars: () => void;
  onBack: () => void;
}

export const SavedItemsView: React.FC<SavedItemsViewProps> = ({
  savedQuestions,
  savedCigars,
  onDeleteQuestion,
  onDeleteCigar,
  onAddCustomQuestion,
  onViewCigar,
  onDiscoverCigars,
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState<'questions' | 'cigars'>('questions');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');

  // Filter items by search
  const filteredQuestions = savedQuestions.filter(q =>
    q.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredCigars = savedCigars.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.brand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddQuestion = () => {
    if (newQuestion.trim()) {
      onAddCustomQuestion(newQuestion.trim());
      setNewQuestion('');
      setShowAddQuestion(false);
    }
  };

  const strengthColors: Record<string, string> = {
    mild: 'bg-green-500/20 text-green-400',
    medium: 'bg-amber-500/20 text-amber-400',
    'medium-full': 'bg-orange-500/20 text-orange-400',
    full: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <span className="text-white font-semibold">My Saved Items</span>
          </div>
          <nav className="flex items-center gap-4 text-sm">
            <button className="text-white/50 hover:text-white">Dashboard</button>
            <button className="text-white/50 hover:text-white">Discover</button>
            <button className="text-white/50 hover:text-white">Festivals</button>
            <button className="text-white/50 hover:text-white">Profile</button>
          </nav>
        </div>
      </div>

      <div className="p-4">
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            <span className="italic">Saved Questions</span>{' '}
            <span className="text-amber-500 italic">& Cigars</span>
          </h1>
          <p className="text-white/60">
            Your personal guide for the Ybor Cigar Fest.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex bg-stone-800 rounded-xl p-1 mb-6">
          <button
            onClick={() => setActiveTab('questions')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'questions'
                ? 'bg-white/10 text-white'
                : 'text-white/50 hover:text-white'
            }`}
          >
            Saved Questions ({savedQuestions.length})
          </button>
          <button
            onClick={() => setActiveTab('cigars')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'cigars'
                ? 'bg-white/10 text-white'
                : 'text-white/50 hover:text-white'
            }`}
          >
            Saved Cigars ({savedCigars.length})
          </button>
        </div>

        {/* Search and Add */}
        <div className="flex gap-2 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder="Search saved items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-stone-800 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-amber-500"
            />
          </div>
          {activeTab === 'questions' && (
            <button
              onClick={() => setShowAddQuestion(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-stone-900 font-medium text-sm hover:bg-amber-400"
            >
              <Plus className="w-4 h-4" />
              Add Custom Question
            </button>
          )}
        </div>

        {/* Add Question Modal */}
        {showAddQuestion && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-stone-800 rounded-2xl p-6 w-full max-w-md">
              <h3 className="text-xl font-bold text-white mb-4">Add Custom Question</h3>
              <textarea
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Type your question for vendors..."
                className="w-full h-32 p-4 rounded-xl bg-stone-700 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-amber-500 resize-none"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowAddQuestion(false)}
                  className="px-4 py-2 rounded-lg text-white/70 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddQuestion}
                  className="px-4 py-2 rounded-lg bg-amber-500 text-stone-900 font-medium hover:bg-amber-400"
                >
                  Add Question
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-white mb-3">Saved Questions</h2>
            {filteredQuestions.length > 0 ? (
              filteredQuestions.map((question, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between p-4 rounded-xl bg-stone-800 border border-white/10 hover:border-white/20 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-white">{question}</p>
                  </div>
                  <button
                    onClick={() => onDeleteQuestion(index)}
                    className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all"
                  >
                    <Trash2 className="w-4 h-4 text-white/50 hover:text-red-400" />
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-white/50">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No saved questions yet</p>
                <p className="text-sm mt-1">Questions you save will appear here</p>
              </div>
            )}
          </div>
        )}

        {/* Cigars Tab */}
        {activeTab === 'cigars' && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-3">Saved Cigars</h2>
            {filteredCigars.length > 0 ? (
              <div className="space-y-3">
                {filteredCigars.map(cigar => (
                  <div
                    key={cigar.id}
                    onClick={() => onViewCigar(cigar)}
                    className="flex items-center justify-between p-4 rounded-xl bg-stone-800 border border-white/10 hover:border-white/20 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Star className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{cigar.name}</h3>
                        <p className="text-white/50 text-sm">{cigar.brand}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1.5">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${strengthColors[cigar.strength]}`}>
                          {cigar.strength.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        {cigar.flavors.slice(0, 1).map(flavor => (
                          <span key={flavor} className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/60">
                            {flavor.charAt(0).toUpperCase() + flavor.slice(1)}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteCigar(cigar.id); }}
                        className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all"
                      >
                        <Bookmark className="w-4 h-4 text-amber-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-stone-800/50 rounded-2xl border border-white/10">
                <Bookmark className="w-10 h-10 mx-auto mb-3 text-white/30" />
                <h3 className="text-white font-medium mb-1">No Saved Cigars Yet</h3>
                <p className="text-white/50 text-sm mb-4">
                  When you save a cigar recommendation, it<br />will appear here for easy access.
                </p>
                <button
                  onClick={onDiscoverCigars}
                  className="px-6 py-2.5 rounded-xl bg-amber-500 text-stone-900 font-medium text-sm hover:bg-amber-400"
                >
                  Discover Cigars
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedItemsView;
